import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, LogOut, ChevronDown, Calendar, Dumbbell, Wallet, Repeat } from 'lucide-react'
import { Body, Caption } from '@meiuca/ui-components'
import type { User as UserType } from '@/types/auth'

interface UserMenuProps {
  user: UserType
  onLogout: () => void
}

export function UserMenu({ user, onLogout }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const getInitials = (name?: string | null) => {
    if (!name) return 'U'
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return 'U'
    return parts
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const displayName = user.nickname || user.name || 'Usuário'

  return (
    <div className="relative" ref={menuRef}>
      {/* User Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background-secondary transition-colors"
      >
        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center">
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={user.name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span className="text-white text-sm font-semibold">
              {getInitials(user.name)}
            </span>
          )}
        </div>

        {/* Name (hidden on mobile) */}
        <span className="hidden md:block text-sm font-medium text-text-primary max-w-[120px] truncate">
          {displayName}
        </span>

        {/* Chevron */}
        <ChevronDown
          className={`w-4 h-4 text-text-secondary transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-background-white rounded-lg shadow-lg border border-border-light overflow-hidden z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-border-light bg-background-secondary">
            <Body className="font-semibold text-text-primary truncate">
              {user.name}
            </Body>
            {user.nickname && (
              <Caption className="text-text-secondary truncate">
                @{user.nickname}
              </Caption>
            )}
            {user.phoneNumber && (
              <Caption className="text-text-tertiary">
                {user.phoneNumber}
              </Caption>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {/* My Reservations */}
            <button
              className="w-full flex items-center gap-3 px-4 py-2 text-text-secondary hover:bg-background-secondary transition-colors"
              onClick={() => {
                setIsOpen(false)
                navigate('/reservations')
              }}
            >
              <Calendar className="w-4 h-4" />
              <span className="text-sm">Minhas reservas</span>
            </button>

            {/* My Workouts */}
            <button
              className="w-full flex items-center gap-3 px-4 py-2 text-text-secondary hover:bg-background-secondary transition-colors"
              onClick={() => {
                setIsOpen(false)
                navigate('/treinos')
              }}
            >
              <Dumbbell className="w-4 h-4" />
              <span className="text-sm">Meus treinos</span>
            </button>

            {/* My Plans */}
            <button
              className="w-full flex items-center gap-3 px-4 py-2 text-text-secondary hover:bg-background-secondary transition-colors"
              onClick={() => {
                setIsOpen(false)
                navigate('/planos')
              }}
            >
              <Wallet className="w-4 h-4" />
              <span className="text-sm">Meus planos</span>
            </button>

            {/* My Memberships (arena) */}
            <button
              className="w-full flex items-center gap-3 px-4 py-2 text-text-secondary hover:bg-background-secondary transition-colors"
              onClick={() => {
                setIsOpen(false)
                navigate('/mensalistas')
              }}
            >
              <Repeat className="w-4 h-4" />
              <span className="text-sm">Meus horários fixos</span>
            </button>

            {/* Profile Link */}
            <button
              className="w-full flex items-center gap-3 px-4 py-2 text-text-secondary hover:bg-background-secondary transition-colors"
              onClick={() => {
                setIsOpen(false)
                navigate('/perfil')
              }}
            >
              <User className="w-4 h-4" />
              <span className="text-sm">Meu perfil</span>
            </button>

            {/* Divider */}
            <div className="h-px bg-border-light my-2" />

            {/* Logout */}
            <button
              className="w-full flex items-center gap-3 px-4 py-2 text-feedback-error hover:bg-feedback-error/10 transition-colors"
              onClick={() => {
                setIsOpen(false)
                onLogout()
              }}
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">Sair</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
